import React, { useState } from 'react';
import { 
  Button, 
  Form, 
  Input, 
  Table, 
  Tooltip, 
  Pagination, 
  Dropdown, 
  Space, 
  Card, 
  Switch, 
  Menu, 
  message 
} from 'antd';
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css'; // 仅为此页面导入 Ant Design 样式
import API from '../services/api'; // 导入统一的API实例
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';

const AdminAttackQuery = () => {
  const [ip, setIp] = useState('');  // 管理输入的IP地址
  const [attackLogs, setAttackLogs] = useState([]); // 原始日志数据
  const [loading, setLoading] = useState(false); // 控制加载动画
  const [currentPage, setCurrentPage] = useState(1); // 当前页码
  const [exportLoading, setExportLoading] = useState(false); // 控制导出按钮加载状态
  const logsPerPage = 10; // 每页显示的日志数量
  const [hasSearched, setHasSearched] = useState(false); // 控制是否显示查询结果
  const [exportType, setExportType] = useState('all'); // 'all' 或 'current'
  const [pdfCompress, setPdfCompress] = useState(true); // 控制PDF压缩选项

  // IP地址格式验证
  const validateIP = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleAttackQuery = async () => {
    const trimmedIp = ip.trim();
    
    if (!trimmedIp) {
      message.error('请输入IP地址');
      return;
    }
    
    if (!validateIP(trimmedIp)) {
      message.error('请输入正确的IP地址格式（如：192.168.1.1）');
      return;
    }
    
    setLoading(true); // 开启加载动画
    setHasSearched(false); // 重置查询状态
    try {
      const response = await API.get('/api/admin/ip-attack-lookup', {
        params: { ip: trimmedIp },  // 传递验证过的IP地址作为查询参数
      });

      // 检查响应数据结构
      if (response.data.message && !response.data.attackLogs) {
        // 后端返回消息表示没有找到数据
        message.info({
          content: `未找到IP地址 ${trimmedIp} 的攻击日志记录`,
          duration: 4
        });
        setAttackLogs([]);
      } else {
        const logs = response.data.attackLogs || [];
        if (logs.length === 0) {
          message.info({
            content: `未找到IP地址 ${trimmedIp} 的攻击日志记录`,
            duration: 4
          });
          setAttackLogs([]);  // 如果没有匹配的数据，清空attackLogs
        } else {
          // 按开始时间降序排序，最新的在最前面
          const sortedLogs = logs.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
          setAttackLogs(sortedLogs); // 保存排序后的日志数据
          setCurrentPage(1); // 每次查询后重置页码为1
          message.success(`查询成功，找到 ${logs.length} 条攻击日志`);
        }
      }
      setHasSearched(true); // 标记为已进行查询
    } catch (error) {
      console.error('获取攻击日志时出错:', error);
      
      // 直接显示后端返回的错误内容
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.request) {
        message.error('网络连接失败，请检查网络状态');
      } else {
        message.error('查询出错，请稍后重试');
      }
      
      // 发生真正错误时也要标记为已搜索，以便显示错误状态
      setHasSearched(true);
      setAttackLogs([]);
    }
    setLoading(false); // 关闭加载动画
  };

  const convertDurationToChineseWithSeconds = (duration) => {
    const match = duration.match(/(\d+)(seconds?|minutes?|hours?)/g);
    let result = '';
    if (!match) return duration;
  
    match.forEach((time) => {
      const [value, unit] = time.match(/\d+|[a-zA-Z]+/g);
      switch (unit) {
        case 'second':
        case 'seconds':
          result += `${value}秒`;
          break;
        case 'minute':
        case 'minutes':
          result += `${value}分钟 `;
          break;
        case 'hour':
        case 'hours':
          result += `${value}小时 `;
          break;
        default:
          result += time;
      }
    });
  
    return result.trim();
  };

  const convertToGbps = (sizeInMbps) => {
    return (sizeInMbps / 1000).toFixed(2);
  };

  const formatDateTimeWithoutMilliseconds = (dateTimeStr) => {
    return dateTimeStr.split('.')[0];
  };

  const convertToGbpsFromBytes = (sizeInBytes) => {
    const sizeInBits = sizeInBytes * 8;
    const sizeInMbps = sizeInBits / 1e6;
    return (sizeInMbps / 1000).toFixed(2);
  };

  const calculateInterceptRate = (size, dropSize) => {
    const peakTrafficGbps = convertToGbps(size);
    const dropTrafficGbps = convertToGbpsFromBytes(dropSize);
    
    if (peakTrafficGbps === 0) return '0.00%';
    
    // 计算拦截率并保留两位小数，不进行进位
    let interceptRate = (dropTrafficGbps / peakTrafficGbps) * 100;
    
    // 将拦截率转换为字符串，并保留两位小数，防止进位
    interceptRate = interceptRate.toString();
    const dotIndex = interceptRate.indexOf('.');
    
    // 如果有小数点，截取小数点后两位；如果没有小数点，则补充".00"
    if (dotIndex !== -1) {
      interceptRate = interceptRate.substring(0, dotIndex + 3); // 保留两位小数
    } else {
      interceptRate = interceptRate + '.00'; // 没有小数点时补充.00
    }
    
    return `${interceptRate}%`;
  };

  // 分页逻辑，显示所有日志
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = attackLogs.slice(indexOfFirstLog, indexOfLastLog);

  const totalPages = Math.ceil(attackLogs.length / logsPerPage);

  // 根据导出类型获取要导出的日志数据
  const getLogsToExport = () => {
    return exportType === 'all' ? attackLogs : currentLogs;
  };

  // 格式化日志数据用于导出
  const formatLogsForExport = () => {
    // 根据导出类型选择数据
    const logsToExport = getLogsToExport();
    return logsToExport.map(log => ({
      '目标IP': log.s_ip,
      '目的端口': log.s_port_list,
      '攻击源IP': log.c_ip_list,
      '源端口': log.c_port_list,
      '状态': log.status === 1 ? '正在攻击' : '攻击结束',
      '防护方式': log.deftype,
      '峰值流量 (Gbps)': `${convertToGbps(log.size)} Gbps`,
      '拦截流量 (Gbps)': `${convertToGbpsFromBytes(log.drop_size)} Gbps`,
      '拦截率': calculateInterceptRate(log.size, log.drop_size),
      '包类型': log.packet_type,
      '开始时间': formatDateTimeWithoutMilliseconds(log.start_time),
      '结束时间': log.end_time,
      '持续时间': convertDurationToChineseWithSeconds(log.duration)
    }));
  };

  // 导出Excel功能
  const exportToExcel = () => {
    const logsToExport = getLogsToExport();
    if (logsToExport.length === 0) {
      message.error('没有数据可导出');
      return;
    }

    setExportLoading(true);
    try {
      const formattedLogs = formatLogsForExport();
      const worksheet = utils.json_to_sheet(formattedLogs);
      
      // 设置列宽
      const columnWidths = [
        { wch: 15 }, // 目标IP
        { wch: 30 }, // 目的端口 - 增加宽度
        { wch: 50 }, // 攻击源IP - 增加宽度
        { wch: 30 }, // 源端口 - 增加宽度
        { wch: 10 }, // 状态
        { wch: 15 }, // 防护方式
        { wch: 15 }, // 峰值流量
        { wch: 15 }, // 拦截流量
        { wch: 10 }, // 拦截率
        { wch: 10 }, // 包类型
        { wch: 20 }, // 开始时间
        { wch: 20 }, // 结束时间
        { wch: 15 }  // 持续时间
      ];
      worksheet['!cols'] = columnWidths;
      
      // 创建工作簿并添加工作表
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, '攻击日志');
      
      // 转换为二进制格式并下载
      const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // 设置文件名称包含当前北京时间日期和导出类型
      // 创建北京时间的日期对象
      const date = new Date();
      const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000)); // 转换为北京时间
      const formattedDate = `${beijingTime.getUTCFullYear()}-${String(beijingTime.getUTCMonth() + 1).padStart(2, '0')}-${String(beijingTime.getUTCDate()).padStart(2, '0')}`;
      const exportTypeText = exportType === 'all' ? '全部' : '当前页';
      
      // 生成6位随机字符
      const randomStr = Math.random().toString(36).substring(2, 8);
      
      const fileName = `攻击日志_${ip}_${exportTypeText}_${formattedDate}_${randomStr}.xlsx`;
      
      saveAs(blob, fileName);
      message.success('Excel导出成功');
    } catch (error) {
      console.error('导出Excel失败:', error);
      message.error('导出Excel失败，请重试');
    } finally {
      setExportLoading(false);
    }
  };

  // 导出PDF功能
  const exportToPDF = () => {
    const logsToExport = getLogsToExport();
    if (logsToExport.length === 0) {
      message.error('没有数据可导出');
      return;
    }

    setExportLoading(true);
    
    // 调用后端API进行PDF导出
    const exportData = {
      ip: ip,
      exportType: exportType === 'all' ? 'full' : 'page',
      compress: pdfCompress // 使用压缩选项状态
    };
    
    // 使用Blob API处理二进制响应
    API.post('/api/admin/export-attack-logs-pdf', exportData, { responseType: 'blob' })
      .then(response => {
        // 创建下载链接
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // 获取当前北京时间用于文件名
        const date = new Date();
        const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000)); // 转换为北京时间
        const formattedDate = `${beijingTime.getUTCFullYear()}-${String(beijingTime.getUTCMonth() + 1).padStart(2, '0')}-${String(beijingTime.getUTCDate()).padStart(2, '0')}`;
        const exportTypeText = exportType === 'all' ? 'All' : 'CurrentPage';
        
        // 生成6位随机字符
        const randomStr = Math.random().toString(36).substring(2, 8);
        
        const fileName = `DDoS_Attack_Report_${ip.replace(/\./g, '_')}_${exportTypeText}_${formattedDate}_${randomStr}.pdf`;
        
        // 使用file-saver库保存文件
        saveAs(blob, fileName);
        message.success('PDF导出成功');
        
        setExportLoading(false);
      })
      .catch(error => {
        console.error('导出PDF失败:', error);
        let errorMsg = '导出PDF失败';
        
        // 尝试解析错误消息（如果是JSON格式）
        if (error.response && error.response.data) {
          try {
            // 对于Blob类型的错误响应，需要先读取内容
            const reader = new FileReader();
            reader.onload = function() {
              try {
                const errorData = JSON.parse(reader.result);
                message.error(errorData.message || errorMsg);
              } catch (e) {
                message.error(errorMsg);
              }
            };
            reader.readAsText(error.response.data);
          } catch (e) {
            message.error(errorMsg);
          }
        } else {
          message.error(errorMsg);
        }
        
        setExportLoading(false);
      });
  };

  // 表格列定义
  const columns = [
    {
      title: '目标IP',
      dataIndex: 's_ip',
      key: 's_ip',
      width: 130,
      align: 'center',
    },
    {
      title: '目的端口',
      dataIndex: 's_port_list',
      key: 's_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '攻击源IP',
      dataIndex: 'c_ip_list',
      key: 'c_ip_list',
      width: 130,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 15 ? `${text.slice(0, 12)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '源端口',
      dataIndex: 'c_port_list',
      key: 'c_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (status) => (
        <span 
          style={{ 
            color: status === 1 ? '#ff4d4f' : '#52c41a',
            fontWeight: 500
          }}
        >
          {status === 1 ? '正在攻击' : '攻击结束'}
        </span>
      ),
    },
    {
      title: '防护方式',
      dataIndex: 'deftype',
      key: 'deftype',
      width: 220,
      align: 'center',
    },
    {
      title: '峰值流量',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      align: 'center',
      render: (size) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>
          {convertToGbps(size)} Gbps
        </span>
      ),
    },
    {
      title: '拦截流量',
      dataIndex: 'drop_size',
      key: 'drop_size',
      width: 120,
      align: 'center',
      render: (dropSize) => (
        <span style={{ fontWeight: 500, color: '#52c41a' }}>
          {convertToGbpsFromBytes(dropSize)} Gbps
        </span>
      ),
    },
    {
      title: '拦截率',
      key: 'intercept_rate',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const rate = calculateInterceptRate(record.size, record.drop_size);
        const percentage = parseFloat(rate.replace('%', ''));
        const color = percentage > 80 ? '#52c41a' : percentage > 50 ? '#faad14' : '#ff4d4f';
        return (
          <span style={{ fontWeight: 500, color }}>
            {rate}
          </span>
        );
      },
    },
    {
      title: '包类型',
      dataIndex: 'packet_type',
      key: 'packet_type',
      width: 90,
      align: 'center',
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 160,
      align: 'center',
      render: (time) => formatDateTimeWithoutMilliseconds(time),
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 160,
      align: 'center',
    },
    {
      title: '持续时间',
      dataIndex: 'duration',
      key: 'duration',
      width: 130,
      align: 'center',
      render: (duration) => (
        <span style={{ fontWeight: 500 }}>
          {convertDurationToChineseWithSeconds(duration)}
        </span>
      ),
    },
  ];

  // 导出选项菜单
  const exportMenu = (
    <Menu
      items={[
        {
          key: 'export-all',
          label: '导出所有数据',
          onClick: () => setExportType('all'),
          style: { backgroundColor: exportType === 'all' ? '#f0f0f0' : 'transparent' },
        },
        {
          key: 'export-current',
          label: '仅导出当前页',
          onClick: () => setExportType('current'),
          style: { backgroundColor: exportType === 'current' ? '#f0f0f0' : 'transparent' },
        },
        {
          type: 'divider',
        },
        {
          key: 'pdf-compress',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>PDF压缩</span>
              <Switch
                size="small"
                checked={pdfCompress}
                onChange={(checked) => setPdfCompress(checked)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ),
        },
      ]}
    />
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      handleAttackQuery();
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#fafafa', 
      minHeight: 'calc(100vh - 64px)'
    }}>
      <div style={{ maxWidth: '100%', margin: '0 auto' }}>
        {/* 页面标题 */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          background: '#fff',
          padding: '24px 20px',
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: '24px', 
            fontWeight: '500',
            color: '#262626'
          }}>
            管理员攻击日志查询
          </h1>
          <p style={{ 
            margin: '8px 0 0 0', 
            fontSize: '14px', 
            color: '#8c8c8c'
          }}>
            DDoS防护系统 - 大墙
          </p>
        </div>
        
        {/* 搜索区域 */}
        <Card 
          style={{ 
            marginBottom: '20px',
            border: '1px solid #e8e8e8'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Form layout="inline" style={{ justifyContent: 'center' }}>
            <Form.Item>
              <Input.Search
                placeholder="请输入要查询的IP地址"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                onSearch={handleAttackQuery}
                onKeyPress={handleSearch}
                loading={loading}
                style={{ width: '320px' }}
                size="large"
                enterButton="查询"
              />
            </Form.Item>
          </Form>
        </Card>

        {/* 结果区域 */}
        {hasSearched && (
          <Card
            style={{ border: '1px solid #e8e8e8' }}
            bodyStyle={{ padding: '20px' }}
          >
            {attackLogs.length > 0 && (
              <div style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                background: '#f5f5f5',
                borderRadius: '6px'
              }}>
                <div style={{ 
                  color: '#595959', 
                  fontSize: '14px'
                }}>
                  总计 <strong>{attackLogs.length}</strong> 条记录，
                  当前显示第 <strong>{indexOfFirstLog + 1}</strong> - <strong>{Math.min(indexOfLastLog, attackLogs.length)}</strong> 条
                </div>
                <Space size="middle">
                  <Dropdown overlay={exportMenu} trigger={['click']}>
                    <Button icon={<DownloadOutlined />}>
                      导出选项
                    </Button>
                  </Dropdown>
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined />}
                    onClick={exportToExcel}
                    loading={exportLoading}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  >
                    导出Excel
                  </Button>
                  <Button
                    danger
                    icon={<FilePdfOutlined />}
                    onClick={exportToPDF}
                    loading={exportLoading}
                  >
                    导出PDF
                  </Button>
                </Space>
              </div>
            )}
            
            {attackLogs.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '50px 20px', 
                color: '#8c8c8c',
                background: '#fafafa',
                borderRadius: '6px',
                border: '1px dashed #d9d9d9'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }}>🔍</div>
                <div style={{ fontSize: '16px', marginBottom: '8px', color: '#595959' }}>
                  未找到 <strong style={{ color: '#262626' }}>{ip}</strong> 的攻击日志
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  该IP地址可能没有遭受攻击，或者攻击记录已过期
                </div>
              </div>
            ) : (
              <>
                <Table
                  columns={columns}
                  dataSource={currentLogs}
                  pagination={false}
                  scroll={{ x: 1800 }}
                  size="middle"
                  bordered
                  rowKey={(record, index) => index}
                  style={{ borderRadius: '6px', overflow: 'hidden' }}
                />
                
                {totalPages > 1 && (
                  <div style={{ 
                    marginTop: '20px', 
                    textAlign: 'center',
                    padding: '16px',
                    background: '#fafafa',
                    borderRadius: '6px'
                  }}>
                    <Pagination
                      current={currentPage}
                      total={attackLogs.length}
                      pageSize={logsPerPage}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showQuickJumper
                      showTotal={(total, range) => 
                        `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
                      }
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        )}
      </div>
      
      {/* 简化的自定义样式 */}
      <style jsx>{`
        .ant-table-thead > tr > th {
          background-color: #fafafa;
          color: #262626;
          font-weight: 500;
          border-bottom: 2px solid #e8e8e8;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #f5f5f5;
        }
        .ant-table-tbody > tr:nth-child(even) {
          background-color: #fdfdfd;
        }
      `}</style>
    </div>
  );
};

export default AdminAttackQuery;